import { EventDocumentTemplate, EventGeneralSettings, EventLocation, EventType } from '../../types';
import { encodeRemoteFileUrl } from '../utils/eventDocumentUrls';
import { apiService } from './apiService';
import { getCurrentWorkplaceId } from 'core/session/workplaceStore';

/** Workplace IRI for JSON-LD bodies (GET may return `/api/workplaces/n` or `api/workplaces/n`). */
function workplaceIriForEventDocument(doc: EventDocumentTemplate): string {
	const wid = getCurrentWorkplaceId();
	const w = doc.workplace;
	if (w && typeof w === 'string') {
		const t = w.trim();
		if (t.startsWith('/api/')) return t;
		if (t.startsWith('api/')) return `/${t}`;
		return t;
	}
	return `/api/workplaces/${wid}`;
}

/** Body for PATCH …/event-document-templates/{id}/toggle-active (application/merge-patch+json). */
function mergePatchBodyForEventDocumentToggleActive(
	doc: EventDocumentTemplate,
	overrides: Partial<Pick<EventDocumentTemplate, 'name' | 'sortOrder' | 'active'>>
): Record<string, unknown> {
	const body: Record<string, unknown> = {
		workplace: workplaceIriForEventDocument(doc),
		name: overrides.name ?? doc.name,
		sortOrder: overrides.sortOrder ?? doc.sortOrder,
		filePath: doc.filePath ?? '',
		active: overrides.active ?? doc.active,
	};
	if (doc.createdAt) body.createdAt = doc.createdAt;
	if (doc.updatedAt) body.updatedAt = doc.updatedAt;
	return body;
}

function mapEventDocumentTemplateResponse(data: any, fallbackId: number): EventDocumentTemplate {
	const rawPath = data?.filePath ?? data?.file_path ?? '';
	return {
		id: Number(data?.id ?? fallbackId),
		name: data?.name ?? '',
		sortOrder: data?.sortOrder ?? 0,
		filePath: typeof rawPath === 'string' ? rawPath : '',
		active: data?.active ?? true,
		workplace: data?.workplace,
		createdAt: data?.createdAt,
		updatedAt: data?.updatedAt,
	};
}

/**
 * Public URL for a banquet PDF template. REACT_APP_IMAGE_URL is normally `…/uploads`.
 * Files are served from `uploads/event_templates`.
 */
export function getEventDocumentTemplatePdfUrl(filePath: string): string {
	if (!filePath) return '';

	const uploadsRoot = (process.env.REACT_APP_IMAGE_URL || '').replace(/\/$/, '');
	if (!uploadsRoot) return '';

	if (/^https?:\/\//i.test(filePath)) {
		return encodeRemoteFileUrl(filePath);
	}

	let p = filePath.replace(/^\/+/, '');
	if (p.startsWith('uploads/')) {
		p = p.slice('uploads/'.length);
	}

	const normalizeFolder = (segment: string) =>
		segment
			.replace(/^event_document_templates\//, 'event_templates/')
			.replace(/^event-document-templates\//, 'event_templates/')
			.replace(/^event_documents\//, 'event_templates/');

	if (p.includes('/')) {
		return encodeRemoteFileUrl(`${uploadsRoot}/${normalizeFolder(p)}`);
	}

	return encodeRemoteFileUrl(`${uploadsRoot}/event_templates/${p}`);
}

export const eventsService = {
    // Event Locations
    getEventLocations: async (params?: { page?: number; pagination?: boolean; workplace?: number }): Promise<EventLocation[]> => {
        try {
            const workplaceId = params?.workplace ?? getCurrentWorkplaceId();
            const queryParams = {
                page: params?.page ?? 1,
                pagination: params?.pagination ?? false,
                ...(workplaceId && { workplace: workplaceId }),
            };

            const response = await apiService.get('settings_locations', { params: queryParams });
            const data = apiService.responseHandler(response);
            // Handle both array and { member: [...] } formats
            const locations = Array.isArray(data) ? data : (data?.member || []);
            return locations.map((item: any) => ({
                id: item.id?.toString() || '',
                label: item.label || '',
                name: item.label || item.name || '', // Keep name for backward compatibility
                active: item.active ?? true,
                workplace: item.workplace || (workplaceId ? `/api/workplaces/${workplaceId}` : undefined),
            }));
        } catch (error) {
            console.error('Failed to fetch event locations:', error);
            return [];
        }
    },

    addEventLocation: async (location: Omit<EventLocation, 'id'>): Promise<EventLocation> => {
        const workplaceId = getCurrentWorkplaceId();
        const payload = {
            label: location.label || location.name || '',
            active: true, // Always pass true
            workplace: location.workplace || (workplaceId ? `/api/workplaces/${workplaceId}` : undefined),
        };

        const response = await apiService.post('settings_locations', payload);
        const data = apiService.responseHandler(response);
        return {
            id: data.id?.toString() || Math.random().toString(),
            label: data.label || payload.label,
            name: data.label || payload.label, // Keep name for backward compatibility
            active: data.active ?? true,
            workplace: data.workplace || payload.workplace,
        };
    },

    updateEventLocation: async (location: EventLocation): Promise<EventLocation> => {
        const workplaceId = getCurrentWorkplaceId();
        const payload = {
            label: location.label || location.name || '',
            active: true, // Always pass true
            workplace: location.workplace || (workplaceId ? `/api/workplaces/${workplaceId}` : undefined),
        };

        const response = await apiService.put(`settings_locations/${location.id}`, payload);
        const data = apiService.responseHandler(response);
        return {
            id: data.id?.toString() || location.id,
            label: data.label || payload.label,
            name: data.label || payload.label, // Keep name for backward compatibility
            active: data.active ?? true,
            workplace: data.workplace || payload.workplace,
        };
    },

    deleteEventLocation: async (id: string): Promise<boolean> => {
        try {
            await apiService.delete(`settings_locations/${id}`);
            return true;
        } catch (error) {
            console.error('Failed to delete event location:', error);
            return false;
        }
    },

    // Event Types
    getEventTypes: async (params?: { page?: number; itemsPerPage?: number; pagination?: boolean; workplace?: number }): Promise<EventType[]> => {
        try {
            const workplaceId = params?.workplace ?? getCurrentWorkplaceId();
            const queryParams = {
                page: params?.page ?? 1,
                itemsPerPage: params?.itemsPerPage ?? 30,
                pagination: params?.pagination ?? false,
                ...(workplaceId && { workplace: workplaceId }),
            };

            const response = await apiService.get('settings_event_types', { params: queryParams });
            const data = apiService.responseHandler(response);
            // Handle both array and { member: [...] } formats
            const types = Array.isArray(data) ? data : (data?.member || []);
            return types.map((item: any) => ({
                id: item.id?.toString() || '',
                label: item.label || '',
                name: item.label || item.name || '', // Keep name for backward compatibility
                active: item.active ?? true,
                isReservation: item.isReservation ?? false,
                workplace: item.workplace || (workplaceId ? `/api/workplaces/${workplaceId}` : undefined),
            }));
        } catch (error) {
            console.error('Failed to fetch event types:', error);
            return [];
        }
    },

    addEventType: async (type: Omit<EventType, 'id'>): Promise<EventType> => {
        const workplaceId = getCurrentWorkplaceId();
        const payload = {
            label: type.label || type.name || '',
            active: type.active ?? true,
            isReservation: type.isReservation ?? false,
            workplace: type.workplace || (workplaceId ? `/api/workplaces/${workplaceId}` : undefined),
        };

        const response = await apiService.post('settings_event_types', payload);
        const data = apiService.responseHandler(response);
        return {
            id: data.id?.toString() || Math.random().toString(),
            label: data.label || payload.label,
            name: data.label || payload.label, // Keep name for backward compatibility
            active: data.active ?? true,
            isReservation: data.isReservation ?? false,
            workplace: data.workplace || payload.workplace,
        };
    },

    updateEventType: async (type: EventType): Promise<EventType> => {
        const workplaceId = getCurrentWorkplaceId();
        const payload = {
            label: type.label || type.name || '',
            active: type.active ?? true,
            isReservation: type.isReservation ?? false,
            workplace: type.workplace || (workplaceId ? `/api/workplaces/${workplaceId}` : undefined),
        };

        const response = await apiService.put(`settings_event_types/${type.id}`, payload);
        const data = apiService.responseHandler(response);
        return {
            id: data.id?.toString() || type.id,
            label: data.label || payload.label,
            name: data.label || payload.label, // Keep name for backward compatibility
            active: data.active ?? true,
            isReservation: data.isReservation ?? false,
            workplace: data.workplace || payload.workplace,
        };
    },

    deleteEventType: async (id: string): Promise<boolean> => {
        try {
            await apiService.delete(`settings_event_types/${id}`);
            return true;
        } catch (error) {
            console.error('Failed to delete event type:', error);
            return false;
        }
    },

    // Event General Settings
    getEventSettings: async (): Promise<EventGeneralSettings | null> => {
        const workplaceId = getCurrentWorkplaceId();
        try {
            const response = await apiService.get('event_settings', {
                params: { pagination: false, ...(workplaceId && { workplace: workplaceId }) },
            });
            const data = apiService.responseHandler(response);
            const items = Array.isArray(data) ? data : (data?.member || []);
            return items.length > 0 ? items[0] : null;
        } catch (error) {
            console.error('Failed to fetch event settings:', error);
            return null;
        }
    },

    saveEventSettings: async (
        settings: EventGeneralSettings,
    ): Promise<EventGeneralSettings> => {
        const workplaceId = getCurrentWorkplaceId();
        const payload = {
            workplace: settings.workplace || (workplaceId ? `/api/workplaces/${workplaceId}` : null),
            maximumReservationsPerTimeSlot: settings.maximumReservationsPerTimeSlot,
            maxReservationSize: settings.maxReservationSize,
            reservationSlotDuration: settings.reservationSlotDuration,
        };
        const response = await apiService.post('event_settings', payload);
        return apiService.responseHandler(response);
    },

    // Banquet event order — PDF templates (multipart POST, collection GET)
    getEventDocumentTemplates: async (params?: { workplace?: number }): Promise<EventDocumentTemplate[]> => {
        try {
            const workplaceId = params?.workplace ?? getCurrentWorkplaceId();
            const queryParams = new URLSearchParams();
            queryParams.append('pagination', 'false');
            queryParams.append('order[sortOrder]', 'asc');
            if (workplaceId && !Number.isNaN(workplaceId)) {
                queryParams.append('workplace', String(workplaceId));
            }
            const response = await apiService.get(`event_document_templates?${queryParams.toString()}`);
            const data = apiService.responseHandler(response);
            const raw = Array.isArray(data) ? data : data?.member || [];
            return raw.map((item: any) => ({
                id: Number(item.id),
                name: item.name ?? '',
                sortOrder: item.sortOrder ?? 0,
                filePath: item.filePath ?? item.file_path ?? '',
                active: item.active ?? true,
                workplace: item.workplace,
                createdAt: item.createdAt,
                updatedAt: item.updatedAt,
            }));
        } catch (error) {
            console.error('Failed to fetch event document templates:', error);
            return [];
        }
    },

    uploadEventDocumentTemplate: async (
        file: File,
        name: string,
        sortOrder: number,
        workplaceId?: number,
        active: boolean = true
    ): Promise<EventDocumentTemplate> => {
        const wid = workplaceId ?? getCurrentWorkplaceId();
        const formData = new FormData();
        formData.append('file', file);
        formData.append('name', name);
        formData.append('sortOrder', String(sortOrder));
        formData.append('workplace', String(wid));
        formData.append('active', active ? '1' : '0');
        const response = await apiService.postUpload('event-document-templates', formData);
        const data = apiService.responseHandler(response);
        return {
            id: Number(data.id),
            name: data.name ?? name,
            sortOrder: data.sortOrder ?? sortOrder,
            filePath: data.filePath ?? '',
            active: data.active ?? true,
            workplace: data.workplace,
        };
    },

    /**
     * Replace the PDF file for an existing template (POST multipart).
     * Adjust the path if your API uses a different custom operation name.
     */
    replaceEventDocumentTemplateFile: async (
        documentId: number,
        file: File
    ): Promise<EventDocumentTemplate> => {
        const formData = new FormData();
        formData.append('file', file);
        const response = await apiService.postUpload(`event-document-templates/${documentId}`, formData);
        const data = apiService.responseHandler(response);
        return mapEventDocumentTemplateResponse(data, documentId);
    },

    /** POST …/event-document-templates/{id} (multipart) — update name / sortOrder / active. File is null when unchanged. */
    updateEventDocumentTemplate: async (
        document: EventDocumentTemplate,
        updates: { name: string; sortOrder: number; active: boolean }
    ): Promise<EventDocumentTemplate> => {
        try {
            const wid = getCurrentWorkplaceId();
            const formData = new FormData();
            formData.append('file', ''); // no file change — pass null
            formData.append('name', updates.name);
            formData.append('sortOrder', String(updates.sortOrder));
            formData.append('workplace', String(wid));
            formData.append('active', updates.active ? '1' : '0');
            const response = await apiService.postUpload(
                `event-document-templates/${document.id}`,
                formData
            );
            const data = apiService.responseHandler(response);
            return mapEventDocumentTemplateResponse(data, document.id);
        } catch (err: any) {
            const msg =
                typeof err === 'string'
                    ? err
                    : err?.['hydra:description'] || err?.message || 'Failed to update document';
            throw new Error(msg);
        }
    },

    /** PATCH …/toggle-active — flip active (chip). */
    toggleEventDocumentTemplateActive: async (doc: EventDocumentTemplate): Promise<boolean> => {
        try {
            const body = mergePatchBodyForEventDocumentToggleActive(doc, {
                active: !doc.active,
            });
            const response = await apiService.patch(
                `event-document-templates/${doc.id}/toggle-active`,
                body
            );
            apiService.responseHandler(response);
            return true;
        } catch (error) {
            console.error('Failed to toggle event document template active:', error);
            return false;
        }
    },

    deleteEventDocumentTemplate: async (id: number): Promise<boolean> => {
        try {
            await apiService.delete(`event_document_templates/${id}`);
            return true;
        } catch (error) {
            console.error('Failed to delete event document template:', error);
            return false;
        }
    },
};

